import mapboxgl from 'mapbox-gl';
import { useEffect } from 'react';
import type { MapRef } from 'react-map-gl';

const CLUSTER_LAYERS = ['farmers-clusters', 'forests-clusters'];
const STYLE_ID = 'moyee-cluster-tooltip-styles';

// Dashboard.tsx forces every Mapbox popup to a 200px photo card; the count
// tooltip opts out through its own class.
const TOOLTIP_CSS = `
  /* The tooltip must never sit between the pointer and the map: a popup that
     catches the mousedown swallows the cluster click. */
  .cluster-count-tooltip {
    pointer-events: none;
  }
  .cluster-count-tooltip .mapboxgl-popup-content {
    width: auto !important;
    padding: 2px 8px !important;
    border-radius: 6px !important;
    font-size: 13px;
  }
`;

/**
 * Desktop only: hovering a cluster shows how many markers it holds (the count
 * is no longer printed on the cluster). Touch devices have no hover, so they
 * get nothing here - tapping a cluster zooms in instead.
 */
export const useClusterTooltip = (mapRef: React.RefObject<MapRef | null>, active: boolean) => {
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!active || !map || !window.matchMedia('(hover: hover)').matches) return;

    if (!document.getElementById(STYLE_ID)) {
      const el = document.createElement('style');
      el.id = STYLE_ID;
      el.textContent = TOOLTIP_CSS;
      document.head.appendChild(el);
    }

    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      anchor: 'bottom',
      className: 'cluster-count-tooltip',
    });

    const onMove = (e: mapboxgl.MapLayerMouseEvent) => {
      const feature = e.features?.[0];
      const count = feature?.properties?.point_count;
      if (typeof count !== 'number' || feature?.geometry.type !== 'Point') {
        popup.remove();
        return;
      }
      popup
        .setLngLat(feature.geometry.coordinates as [number, number])
        .setOffset(18)
        .setText(count.toLocaleString('en-US'))
        .addTo(map);
    };
    const onLeave = () => popup.remove();

    CLUSTER_LAYERS.forEach((layer) => {
      map.on('mousemove', layer, onMove);
      map.on('mouseleave', layer, onLeave);
    });
    // A click zooms into the cluster, which then no longer exists.
    map.on('click', onLeave);

    return () => {
      CLUSTER_LAYERS.forEach((layer) => {
        map.off('mousemove', layer, onMove);
        map.off('mouseleave', layer, onLeave);
      });
      map.off('click', onLeave);
      popup.remove();
    };
  }, [mapRef, active]);
};
